import React, { useState, useEffect, useRef } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import PlaceAutocompleteInput from '../components/PlaceAutocompleteInput';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const API_BASE = process.env.REACT_APP_API_ENDPOINT;

// Routing providers to compare. Colors are used for the map polylines and the
// result table swatches — kept visually distinct so overlapping routes read.
const PROVIDERS = [
  { id: 'google', label: 'Google Maps', color: '#4285F4' },
  { id: 'mapbox', label: 'Mapbox', color: '#F59E0B' },
  { id: 'ors', label: 'OpenRouteService', color: '#10B981' },
  { id: 'manual', label: 'Manuel (Haversine ×1.5)', color: '#A855F7' },
];
const COLOR_BY_ID = PROVIDERS.reduce((acc, p) => { acc[p.id] = p.color; return acc; }, {});

const formatDistance = (km) => (km == null ? '-' : `${km.toFixed(3)} km`);
const formatDuration = (sec) => {
  if (sec == null) return '-';
  const mins = Math.round(sec / 60);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60} min`;
};
const formatTariff = (t) => (t == null ? '-' : `${Math.round(t).toLocaleString('fr-FR')} FCFA`);

// ─── Map: overlays each provider's returned geometry as a colored polyline ────
const RouteDebugMap = ({ points, results, visible }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const overlaysRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: { lat: points.plat || 6.37, lng: points.plng || 2.34 },
      mapId: 'DEMO_MAP_ID',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      keyboardShortcuts: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.google) return;

    // Clear previous overlays
    overlaysRef.current.forEach((o) => {
      if (typeof o.setMap === 'function') o.setMap(null);
      else o.map = null;
    });
    overlaysRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoint = false;

    const addMarker = (lat, lng, label, bg) => {
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
      const el = document.createElement('div');
      el.style.cssText = `width:26px;height:26px;border-radius:50%;background:${bg};border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;font-family:Arial,sans-serif`;
      el.textContent = label;
      const m = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng }, map, content: el,
      });
      overlaysRef.current.push(m);
      bounds.extend({ lat, lng });
      hasPoint = true;
    };
    addMarker(points.plat, points.plng, 'A', '#10B981');
    addMarker(points.dlat, points.dlng, 'B', '#0F172A');

    (results || []).forEach((r) => {
      if (!r.ok || !Array.isArray(r.geometry) || !visible[r.provider]) return;
      const path = r.geometry.map(([lat, lng]) => ({ lat, lng }));
      if (path.length === 0) return;
      const line = new window.google.maps.Polyline({
        path,
        map,
        strokeColor: COLOR_BY_ID[r.provider] || '#888',
        strokeWeight: 5,
        strokeOpacity: 0.7,
      });
      overlaysRef.current.push(line);
      path.forEach((p) => bounds.extend(p));
      hasPoint = true;
    });

    if (hasPoint) map.fitBounds(bounds, 60);
  }, [points, results, visible]);

  return <div ref={mapRef} className="w-full h-full min-h-[420px]" />;
};

const RouteDebugContent = () => {
  const [country, setCountry] = useState('Benin');
  const [pickupAddr, setPickupAddr] = useState('');
  const [dropAddr, setDropAddr] = useState('');
  // Coordinates are the source of truth; the autocomplete just writes into them.
  const [plat, setPlat] = useState('6.42783');
  const [plng, setPlng] = useState('2.31401');
  const [dlat, setDlat] = useState('6.378263');
  const [dlng, setDlng] = useState('2.3430777');
  const [selected, setSelected] = useState({ google: true, mapbox: true, ors: true, manual: true });

  const [points, setPoints] = useState({ plat: 6.42783, plng: 2.31401, dlat: 6.378263, dlng: 2.3430777 });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClassName = 'w-full bg-gray-100 rounded-lg py-3 pl-12 pr-10 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black transition-all';
  const coordInputClass = 'w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black transition-all';

  const toggleProvider = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleCompare = async () => {
    setError('');
    const p = {
      plat: parseFloat(plat), plng: parseFloat(plng),
      dlat: parseFloat(dlat), dlng: parseFloat(dlng),
    };
    if ([p.plat, p.plng, p.dlat, p.dlng].some((v) => Number.isNaN(v))) {
      setError('Coordonnées invalides. Vérifie les latitudes / longitudes.');
      return;
    }
    const providers = PROVIDERS.filter((pr) => selected[pr.id]).map((pr) => pr.id);
    if (providers.length === 0) {
      setError('Sélectionne au moins un provider.');
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/v1/public/debug/route`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...p, providers }),
      });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      setResults(data.results || []);
      setPoints(p);
    } catch (e) {
      setError(e.message || 'Erreur lors de la comparaison');
    } finally {
      setLoading(false);
    }
  };

  // Smallest distance among successful results → highlight the deltas.
  const okResults = (results || []).filter((r) => r.ok);
  const minDistance = okResults.length ? Math.min(...okResults.map((r) => r.distanceKm)) : null;

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-10 max-w-6xl">
        <div className="mb-8">
          <span className="inline-block text-xs font-semibold uppercase tracking-wide text-orange-600 bg-orange-50 rounded-full px-3 py-1 mb-3">Debug</span>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Comparateur de providers de routing</h1>
          <p className="text-gray-600 mt-2">Compare distance, durée et tracé réel de chaque provider (Google, Mapbox, OpenRouteService) pour les deux mêmes points.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* ─── Left: form ─── */}
          <div className="w-full lg:w-1/2 space-y-6">
            {/* Country (for address autocomplete region) */}
            <div className="relative max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Pays (pour l'autocomplétion)</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full bg-gray-100 rounded-lg px-4 pr-10 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black appearance-none cursor-pointer"
              >
                <option value="Benin">🇧🇯 Bénin</option>
                <option value="Togo">🇹🇬 Togo</option>
              </select>
            </div>

            {/* Point A */}
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-gray-900">Point A : ramassage</h2>
              <PlaceAutocompleteInput
                value={pickupAddr}
                onChange={setPickupAddr}
                onPlaceSelect={(place) => {
                  setPickupAddr(place.name || place.address || '');
                  if (place.lat != null) setPlat(String(place.lat));
                  if (place.lng != null) setPlng(String(place.lng));
                }}
                placeholder="Rechercher une adresse (A)"
                country={country}
                icon={<div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>}
                inputClassName={inputClassName}
              />
              <div className="grid grid-cols-2 gap-2">
                <input value={plat} onChange={(e) => setPlat(e.target.value)} placeholder="Latitude A" className={coordInputClass} inputMode="decimal" />
                <input value={plng} onChange={(e) => setPlng(e.target.value)} placeholder="Longitude A" className={coordInputClass} inputMode="decimal" />
              </div>
            </div>

            {/* Point B */}
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-gray-900">Point B : livraison</h2>
              <PlaceAutocompleteInput
                value={dropAddr}
                onChange={setDropAddr}
                onPlaceSelect={(place) => {
                  setDropAddr(place.name || place.address || '');
                  if (place.lat != null) setDlat(String(place.lat));
                  if (place.lng != null) setDlng(String(place.lng));
                }}
                placeholder="Rechercher une adresse (B)"
                country={country}
                icon={<div className="w-2.5 h-2.5 bg-gray-900"></div>}
                inputClassName={inputClassName}
              />
              <div className="grid grid-cols-2 gap-2">
                <input value={dlat} onChange={(e) => setDlat(e.target.value)} placeholder="Latitude B" className={coordInputClass} inputMode="decimal" />
                <input value={dlng} onChange={(e) => setDlng(e.target.value)} placeholder="Longitude B" className={coordInputClass} inputMode="decimal" />
              </div>
            </div>

            {/* Providers */}
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-gray-900">Providers</h2>
              <div className="flex flex-wrap gap-2">
                {PROVIDERS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleProvider(p.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      selected[p.id] ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }}></span>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={handleCompare}
              disabled={loading}
              className={`w-full sm:w-auto px-8 py-3.5 rounded-lg font-medium transition-colors ${
                loading ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-black text-white hover:bg-gray-800'
              }`}
            >
              {loading ? 'Comparaison en cours…' : 'Comparer'}
            </button>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100">{error}</div>
            )}

            {/* Results table */}
            {results && (
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-12 px-4 py-2.5 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <div className="col-span-4">Provider</div>
                  <div className="col-span-3 text-right">Distance</div>
                  <div className="col-span-2 text-right">Durée</div>
                  <div className="col-span-3 text-right">Tarif</div>
                </div>
                {results.map((r) => {
                  const prov = PROVIDERS.find((p) => p.id === r.provider);
                  const delta = r.ok && minDistance != null ? r.distanceKm - minDistance : null;
                  return (
                    <div key={r.provider} className="grid grid-cols-12 items-center px-4 py-3 border-t border-gray-100">
                      <div className="col-span-4 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: prov?.color }}></span>
                        <span className="text-sm font-medium text-gray-900">{prov?.label || r.provider}</span>
                      </div>
                      {r.ok ? (
                        <>
                          <div className="col-span-3 text-right">
                            <span className="text-sm font-semibold text-gray-900">{formatDistance(r.distanceKm)}</span>
                            {delta != null && delta > 0.0005 && (
                              <span className="block text-xs text-orange-600">+{(delta * 1000).toFixed(0)} m</span>
                            )}
                          </div>
                          <div className="col-span-2 text-right text-sm text-gray-700">{formatDuration(r.durationSec)}</div>
                          <div className="col-span-3 text-right text-sm font-semibold text-gray-900">{formatTariff(r.tariff)}</div>
                        </>
                      ) : (
                        <div className="col-span-8 text-right text-sm text-red-600" title={r.error || ''}>Échec</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ─── Right: map ─── */}
          <div className="w-full lg:w-1/2">
            <div className="aspect-square lg:aspect-auto lg:h-[600px] rounded-3xl overflow-hidden bg-gray-100 sticky top-6">
              <RouteDebugMap points={points} results={results} visible={selected} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const renderStatus = (status) => {
  if (status === Status.LOADING) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  if (status === Status.FAILURE) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-red-500">Erreur de chargement de Google Maps</p>
      </div>
    );
  }
  return null;
};

const RouteDebug = () => (
  <Wrapper apiKey={GOOGLE_MAPS_API_KEY} libraries={['places', 'marker']} render={renderStatus}>
    <RouteDebugContent />
  </Wrapper>
);

export default RouteDebug;
